import { useFormMutation } from "@coideo/barrani";
import { useAuth, usePos } from "context/auth-context";
import { DateTime } from "luxon";
import { useQuery, useQueryClient } from "react-query";
import { client } from "utils/api-client";
import {
  CashDrawer,
  type CashDrawerApi,
  cashDrawerFromJson,
  type CashDrawerStatus,
} from "utils/cash-drawer-client";
import { DATE_MEDIUM } from "utils/dates";
import { formatMoney } from "utils/format-money";
import { type CustomError } from "utils/parse-error";

type CashDrawerDetailApi = CashDrawerApi & {
  endDate: string | null;
  pointOfSale: string;
  user: string;
  initialAmount: number;
  closedDrawerAmount: number | null;
  totalAmount: number | null;
  differenceAmount: number | null;
  creditAmount: number;
  debitAmount: number;
  cashAmount: number;
  otherAmount: number;
  purchases: number;
  tickets: number;
  number: number;
  items: CashDrawerItem[] | null;
};

type CashDrawerItem = {
  paymentType: "CASH" | "CREDIT_CARD" | "DEBIT_CARD" | "PIX";
  amount: number;
  purchases: number;
  tickets: number;
  refunds: number;
};

class CashDrawerDetail extends CashDrawer {
  private readonly endDate: DateTime | null;
  private readonly initialAmount: number;
  private readonly closedDrawerAmount: number | null;
  private readonly totalAmount: number | null;
  private readonly differenceAmount: number | null;
  private readonly creditAmount: number;
  private readonly debitAmount: number;
  private readonly cashAmount: number;
  private readonly otherAmount: number;
  private readonly items: CashDrawerItem[] | null;
  readonly purchases: number;
  readonly tickets: number;
  readonly number: number;

  constructor(
    id: number,
    sessionId: string,
    name: string | undefined,
    status: CashDrawerStatus,
    startDate: DateTime,
    endDate: DateTime | null,
    initialAmount: number,
    closedDrawerAmount: number | null,
    totalAmount: number | null,
    differenceAmount: number | null,
    creditAmount: number,
    debitAmount: number,
    cashAmount: number,
    otherAmount: number,
    items: CashDrawerItem[] | null,
    purchases: number,
    tickets: number,
    number: number,
  ) {
    super(id, sessionId, name, status, startDate);
    this.endDate = endDate;
    this.initialAmount = initialAmount;
    this.closedDrawerAmount = closedDrawerAmount;
    this.totalAmount = totalAmount;
    this.differenceAmount = differenceAmount;
    this.creditAmount = creditAmount;
    this.debitAmount = debitAmount;
    this.cashAmount = cashAmount;
    this.otherAmount = otherAmount;
    this.items = items;
    this.purchases = purchases;
    this.tickets = tickets;
    this.number = number;
  }

  getEndDate() {
    return this.endDate ? this.endDate.toLocaleString(DATE_MEDIUM) : null;
  }

  getTotalCardsAmount() {
    return formatMoney(this.creditAmount + this.debitAmount);
  }

  getDebit() {
    return formatMoney(this.debitAmount);
  }

  getCredit() {
    return formatMoney(this.creditAmount);
  }

  getOther() {
    return formatMoney(this.otherAmount);
  }

  getInitialAmount() {
    return formatMoney(this.initialAmount);
  }

  getTotal() {
    return formatMoney(this.totalAmount);
  }

  getCashExpected() {
    return formatMoney(this.initialAmount + this.cashAmount);
  }

  getCash() {
    return formatMoney(this.cashAmount);
  }

  getClosedDrawerAmount() {
    return this.status === "CLOSED" ? formatMoney(this.closedDrawerAmount) : "-";
  }

  getDifference() {
    return this.differenceAmount ?? 0;
  }

  getDifferenceText() {
    return this.differenceAmount === null ? "-" : formatMoney(this.differenceAmount);
  }

  getItems() {
    return this.items ?? [];
  }
}

const cashDrawerDetailFromJson = (data: CashDrawerDetailApi) =>
  new CashDrawerDetail(
    data.id,
    data.sessionId,
    data.name,
    data.status,
    DateTime.fromISO(data.startDate),
    data.endDate ? DateTime.fromISO(data.endDate) : null,
    data.initialAmount,
    data.closedDrawerAmount,
    data.totalAmount,
    data.differenceAmount,
    data.creditAmount,
    data.debitAmount,
    data.cashAmount,
    data.otherAmount,
    data.items,
    data.purchases,
    data.tickets,
    data.number,
  );

function useCashDrawers() {
  const { id } = usePos();

  return useQuery<CashDrawerDetail[], CustomError>(
    [id, "cashDrawers"],
    async ({ signal }) =>
      await client<{ cashDrawers: CashDrawerDetailApi[] }>(`api/pos/${id}/cashDrawers`, {
        signal,
      }).then((r) => r.cashDrawers.map(cashDrawerDetailFromJson)),
  );
}

function useOpenCashDrawers() {
  const { id } = usePos();

  return useQuery<CashDrawer[], CustomError>(
    [id, "cashDrawers", "open"],
    async ({ signal }) =>
      await client<{ cashDrawers: CashDrawerApi[] }>(`api/pos/${id}/cashDrawers/open`, {
        signal,
      }).then((r) => r.cashDrawers.map(cashDrawerFromJson)),
  );
}

function useOpenCashDrawer({ onSuccess }: { onSuccess: () => void }) {
  const queryClient = useQueryClient();
  const { openSession } = useAuth();

  return useFormMutation<void, Error, Parameters<typeof openSession>[0]>(openSession, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(["cashDrawers"]);
      onSuccess();
    },
  });
}

function useCloseCashDrawer({ onSuccess }: { onSuccess: () => void }) {
  const queryClient = useQueryClient();
  const { closeSession } = useAuth();

  return useFormMutation<void, Error, Parameters<typeof closeSession>[0]>(closeSession, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(["cashDrawers"]);
      onSuccess();
    },
  });
}

function useCashDrawer(
  sessionId: string,
  { onSuccess, onError }: { onSuccess: (data: CashDrawerDetail) => void; onError: () => void },
) {
  const { id } = usePos();

  return useQuery(
    [id, "cashDrawers", sessionId],
    async ({ signal }) =>
      await client<CashDrawerDetailApi>(`api/pos/${id}/cashDrawers/${sessionId}`, { signal }).then(
        cashDrawerDetailFromJson,
      ),
    { enabled: Boolean(sessionId), onSuccess, onError },
  );
}

export { useOpenCashDrawer, useCloseCashDrawer, useCashDrawer, useOpenCashDrawers, useCashDrawers };
